import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import PromptModal from "../../components/PromptModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Prompts = () => {
  const classes = useStyles();

  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const authCtx = useContext(AuthContext) || {};
  const user = authCtx.user || {};
  const companyId = user.companyId || localStorage.getItem("companyId");
  const socketManager = useContext(SocketContext);

  const getPrompts = async () => {
    try {
      const { data } = await api.get("/prompt");
      setPrompts(Array.isArray(data?.prompts) ? data.prompts : []);
    } catch (err) {
      toastError(err);
      setPrompts([]);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await getPrompts();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!companyId || !socketManager || !socketManager.getSocket) return undefined;

    const socket = socketManager.getSocket(companyId);
    const eventName = `company-${companyId}-prompt`;

    const onPromptSocket = (data) => {
      if (!data) return;

      if (data.action === "update" || data.action === "create") {
        setPrompts((prev) => {
          const idx = prev.findIndex((p) => p.id === data?.prompt?.id);
          if (idx !== -1) {
            const copy = [...prev];
            copy[idx] = data.prompt;
            return copy;
          }
          return [data.prompt, ...prev];
        });
      }

      if (data.action === "delete" && data.promptId != null) {
        setPrompts((prev) => prev.filter((p) => p.id !== data.promptId));
      }
    };

    socket.on(eventName, onPromptSocket);
    return () => {
      socket.off(eventName, onPromptSocket);
    };
  }, [companyId, socketManager]);

  const handleOpenPromptModal = () => {
    setPromptModalOpen(true);
    setSelectedPrompt(null);
  };

  const handleClosePromptModal = () => {
    setPromptModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleEditPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedPrompt(null);
  };

  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      setPrompts((prev) => prev.filter((p) => p.id !== promptId));
      toast.info(i18n.t(data.message));
    } catch (err) {
      toastError(err);
    }
    setSelectedPrompt(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedPrompt &&
          `${i18n.t("prompts.confirmationModal.deleteTitle")} ${selectedPrompt.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <PromptModal
        open={promptModalOpen}
        onClose={handleClosePromptModal}
        promptId={selectedPrompt?.id}
        refreshPrompts={getPrompts}
      />

      <MainHeader>
        <Title>{i18n.t("prompts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenPromptModal}>
            {i18n.t("prompts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Box p={1} mb={1} width="100%">
        <Typography variant="body2" color="textSecondary" component="div">
          {i18n.t("prompts.openAiHelp")}
        </Typography>
      </Box>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="left">{i18n.t("prompts.table.name")}</TableCell>
              <TableCell align="left">{i18n.t("prompts.table.queue")}</TableCell>
              <TableCell align="left">{i18n.t("prompts.table.max_tokens")}</TableCell>
              <TableCell align="center">{i18n.t("prompts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.map((prompt) => (
              <TableRow key={prompt.id}>
                <TableCell align="left">{prompt.name}</TableCell>
                <TableCell align="left">{prompt.queue?.name}</TableCell>
                <TableCell align="left">{prompt.maxTokens}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEditPrompt(prompt)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading && <TableRowSkeleton columns={4} />}

            {!loading && prompts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum prompt configurado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Prompts;
